/* eslint-disable react/no-unescaped-entities */
import Link from "next/link";
import { Container, Heading } from "@bilar/ui";
import Head from "next/head";
import React from "react";
import { BasePageProps } from "@bilar/common";
import {
	GetStaticProps,
	GetStaticPropsContext,
	GetStaticPropsResult,
} from "next";
import { getBasePagePropsData } from "@bilar/pages-common";
import { getConfig } from "@bilar/config";

type TermsPageProps = {} & BasePageProps;

const TermsPage = (props: TermsPageProps) => {
	const { baseUrl } = getConfig().public;
	return (
		<>
			<Head>
				<title>Bílar.is - Terms of Service</title>
				<link rel="canonical" href={`${baseUrl}/en/terms/en`} key="canonical" />
				<link
					rel="alternate"
					hrefLang="x-default"
					href={`${baseUrl}/terms`}
					key="alternate-x-default"
				/>
				<link
					rel="alternate"
					hrefLang="is"
					href={`${baseUrl}/terms`}
					key="alternate-is"
				/>
				<link
					rel="alternate"
					hrefLang="en"
					href={`${baseUrl}/en/terms/en`}
					key="alternate-en"
				/>
			</Head>
			<Container>
				<Heading as="h1">Terms of Service for Bílar.is</Heading>
				<p>Effective Date: November 16, 2023</p>

				<Heading as="h2">1. Introduction</Heading>
				<p>
					Welcome to Bílar.is. These Terms of Service ("Terms") govern your use
					of our website located at www.bilar.is ("Site") and form a legal
					agreement between Bílar.is and you, the user of our Site.
				</p>
				<Heading as="h3">2. Acceptance of Terms</Heading>
				<p>
					By accessing and using our Site, you agree to be bound by these Terms
					and the <Link href={"/privacy/en"}>Privacy Policy</Link> available on
					our Site. If you do not agree to these Terms or the{" "}
					<Link href={"/privacy/en"}>Privacy Policy</Link>, please do not use
					our services.
				</p>
				<Heading as="h4">3. Use of the Site</Heading>
				<p>
					a. The Site serves as a peer-to-peer platform enabling users to
					independently buy and sell vehicles. Bílar.is is not a party to any
					transactions made between users and does not buy or sell vehicles
					directly.
				</p>
				<p>
					b. You agree to use the Site only for lawful purposes and in a manner
					that does not infringe the rights of, restrict, or inhibit anyone
					else's use and enjoyment of the Site. c. Misuse of the Site, including
					but not limited to, hacking, introducing viruses, or causing
					disruption to the service is strictly prohibited.
				</p>
				<Heading as="h5">4. Registration and Account</Heading>
				<p>
					a. To access certain features of the Site, you may need to register
					and create an account.
				</p>
				<p>
					b. You agree to provide accurate, current, and complete information
					during the registration process and to update such information to keep
					it accurate, current, and complete.
				</p>
				<Heading as="h6">5. Privacy</Heading>
				<p>
					Your use of the Site is also governed by the{" "}
					<Link href={"/privacy/en"}>Privacy Policy</Link> of Bílar.is, which is
					incorporated into these Terms by reference.
				</p>
				<Heading as="h6">6. Modifications to the Terms</Heading>
				<p>
					6. Bílar.is reserves the right to modify these Terms at any time.
					Revised Terms will be posted on the Site and the "Effective Date" will
					be updated to reflect the date of the changes.
				</p>
				<Heading as="h6">7. Intellectual Property</Heading>
				<p>
					All content on the Site, including but not limited to text, graphics,
					logos, images, and software, is the property of Bílar.is or its
					content suppliers and is protected by intellectual property laws.
				</p>
				<p>
					b. Except as explicitly authorized in these Terms, you may not use,
					modify, copy, distribute, transmit, display, perform, reproduce,
					publish, license, create derivative works from, transfer, or sell any
					content obtained from the Site.
				</p>
				<Heading as="h6">8. Disclaimer of Warranties</Heading>
				<p>
					The Site and its content are provided "as is" without warranty of any
					kind, either express or implied, including, but not limited to, the
					implied warranties of merchantability, fitness for a particular
					purpose, or non-infringement.
				</p>
				<Heading as="h6">9. Limitation of Liability</Heading>
				<p>
					Bílar.is will not be liable for any damages of any kind arising from
					the use of this Site, including, but not limited to direct, indirect,
					incidental, punitive, and consequential damages.
				</p>
				<Heading as="h6">10. Governing Law</Heading>
				<p>
					These Terms are governed by and construed in accordance with the laws
					of Icelandic Law.
				</p>
				<Heading as="h6">11. Contact Information</Heading>
				<p>
					For any questions regarding these Terms, please contact Bílar.is at{" "}
					<a href="mailto:support@bilar.is">support@bilar.is</a>
				</p>
			</Container>
		</>
	);
};
export const getStaticProps: GetStaticProps<TermsPageProps> = async (
	ctx: GetStaticPropsContext,
): Promise<GetStaticPropsResult<TermsPageProps>> => {
	// Get Locale and common translations
	const baseProps = await getBasePagePropsData(ctx);

	return {
		props: {
			...baseProps,
		},
		// we will attempt to re-generate the page:
		// - when a request comes in
		// - at most once every second
		//revalidate: 60,
	};
};
export default TermsPage;
